import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FESTA_COUPON_END, FESTA_LANDING_PATH } from '../../utils/festaCampaign';
import './EventsMainPage.css';

export const EVENTS = [
  // 연말 페스타 — 쿠폰 마감(12/31 23:59:59 KST)이 지나면 자동으로 빠진다
  {
    id: 'year-end-festa',
    title: '연말 페스타, PRO 30일 무료',
    subtitle: '카드 등록 없이 생기부 무제한 생성',
    description: '학교 예산으로는 12개월 1인 10만 원, 인원 제한 없어요. 학운위 심의 대상이 아니에요.',
    prize: 'PRO 30일 무료',
    period: '12.31(목)까지',
    endDate: FESTA_COUPON_END,
    path: FESTA_LANDING_PATH,
    highlight: true,
    badge: '연말 한정',
  },
  {
    id: 'free-training',
    title: '무료 연수 신청',
    subtitle: '10명 이상이면 학교로 찾아가요',
    description: '왓퀴즈 활용법을 학교 상황에 맞춰 안내합니다.',
    prize: 'PRO 1개월 무료',
    period: '상시',
    path: '/training',
    special: true,
    badge: '추천',
  },
  {
    id: 'share',
    title: 'SNS 소문내기',
    subtitle: '공유하면 PRO 1개월 + 네이버페이 추첨',
    description: '왓퀴즈를 소개하고 선생님들과 혜택을 나눠보세요.',
    prize: 'PRO 1개월 + 네이버페이',
    period: '상시',
    path: '/events/share',
  },
];

export const getVisibleEvents = (now = Date.now()) => {
  const timestamp = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return EVENTS.filter(event => (
    event.enabled !== false
    && (!event.startDate || timestamp >= new Date(event.startDate).getTime())
    && (!event.endDate || timestamp <= new Date(event.endDate).getTime())
  ));
};

const EventsMainPage = ({ now = Date.now() }) => {
  const navigate = useNavigate();
  const events = getVisibleEvents(now);

  const handleEventClick = (event) => {
    // Google Analytics 이벤트 추적
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'event_main_click', {
        event_category: 'Events',
        event_label: event.id,
        value: 1
      });
    }
    
    // action 함수가 있으면 실행, 아니면 path로 이동
    if (event.action) {
      event.action();
    } else if (event.path) {
      navigate(event.path);
    }
  };

  const renderEventCard = (event) => (
    <article
      aria-label={`${event.title}, ${event.period}, 혜택 ${event.prize}`}
      key={event.id}
      className={`events-page-card ${event.special ? 'events-page-card-special' : ''} ${event.highlight ? 'events-page-card-highlight' : ''}`}
      onClick={() => handleEventClick(event)}
      onKeyDown={(keyboardEvent) => {
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault();
          handleEventClick(event);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="events-page-card-header">
        <div className="events-page-card-icon">
          {event.id === 'year-end-festa' && (
            <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22">
              <rect x="3" y="8" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
              <path d="M5 12v8h14v-8M12 8v12" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
              <path d="M12 8C10.5 5 7 4.5 7 6.5S10 8 12 8Zm0 0c1.5-3 5-3.5 5-1.5S14 8 12 8Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" />
            </svg>
          )}
          {event.id === 'free-training' && (
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          )}
          {event.id === 'share' && (
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
          )}
        </div>
        <div className="events-page-card-meta">
          {event.difficulty && <span className="events-page-card-difficulty">{event.difficulty}</span>}
          <span className="events-page-card-period" style={event.highlight ? { color: '#3182f6', fontWeight: '600' } : {}}>
            {event.period}
          </span>
        </div>
      </div>

      <div className="events-page-card-content">
        <h3 className="events-page-card-title">{event.title}</h3>
        <p className="events-page-card-subtitle">{event.subtitle}</p>
        <p className="events-page-card-desc">{event.description}</p>
      </div>

      <div className="events-page-card-footer">
        <div className="events-page-prize">
          <span className="events-page-prize-label">혜택</span>
          <span className="events-page-prize-value">{event.prize}</span>
        </div>
        <span aria-hidden="true" className="events-page-btn">
          참여하기
        </span>
      </div>

      {event.badge && (
        <div className="events-page-badge">
          {event.badge}
        </div>
      )}
    </article>
  );

  return (
    <div className="events-main-page">
      <Helmet>
        <title>이벤트 - 왓퀴즈</title>
        <meta name="description" content="왓퀴즈에서 진행 중인 이벤트와 참여 혜택을 확인하세요." />
        <meta property="og:title" content="이벤트 - 왓퀴즈" />
        <meta property="og:description" content="왓퀴즈에서 진행 중인 이벤트와 참여 혜택을 확인하세요." />
      </Helmet>

      {/* 이벤트 목록 섹션 */}
      <section className="events-page-list">
        <div className="events-page-container">
          <div className="events-page-header">
            <h1 className="events-page-title">왓퀴즈 이벤트</h1>
            <p className="events-page-subtitle">
              선생님을 위한 특별한 혜택을 확인하세요
            </p>
          </div>

          <div className="events-page-grid">
            {events.map(renderEventCard)}
          </div>

        </div>
      </section>
    </div>
  );
};

export default EventsMainPage; 